/**
 * A live "what would this hand score?" for the round form, BEFORE anything is recorded (Indra, 2026-10-04).
 *
 * It makes exactly the calls `recordWin` makes (validate -> score -> settle) and records nothing, so what the preview shows is what
 * Record Round would then store — it can only differ if the form changed in between, which is why the screen recalculates on every
 * change. Pure: no I/O, no clock, no DOM.
 */

import { getRuleset } from '../rules';
import { describeRejection } from '../rules/rejection';
import type { Tile } from '../rules/tiles';
import type { HandInput, Payment, ScoreResult, WinContext } from '../rules/types';
import type { Game, RoundRecord } from './types';

/** A hand as the form holds it: the winning tile may not be picked yet. */
export interface PreviewHand extends Omit<HandInput, 'winningTile'> {
  winningTile?: Tile;
}

export type WinPreview =
  /** Fewer tiles than a complete hand (14, plus one per kong) — nothing to score yet. */
  | { kind: 'waiting'; have: number; need: number }
  /** Enough tiles, but the winning tile has not been picked. */
  | { kind: 'pickWinningTile' }
  /** The hand and what was ticked contradict each other (a kong replacement win with no kong, a tile used five times ...). */
  | { kind: 'problem'; problems: string[] }
  /** Not a winning shape at all — a tile is wrong or missing. */
  | { kind: 'incomplete'; message: string }
  /** A complete hand, but worth less than the table's minimum. */
  | { kind: 'below_minimum'; message: string; fan: number }
  | { kind: 'ok'; result: ScoreResult; payments: Payment[]; round: RoundRecord };

export function tilesInHand(hand: PreviewHand): { have: number; need: number } {
  const kongs = hand.melds.filter((m) => m.type === 'kong').length;
  return { have: hand.concealed.length + hand.melds.reduce((n, m) => n + m.tiles.length, 0), need: 14 + kongs };
}

export function previewWin(
  game: Pick<Game, 'rulesetId' | 'settings' | 'players' | 'rounds'>,
  hand: PreviewHand,
  ctx: WinContext,
): WinPreview {
  const { have, need } = tilesInHand(hand);
  if (have < need) return { kind: 'waiting', have, need };
  if (!hand.winningTile) return { kind: 'pickWinningTile' };

  const full: HandInput = { ...hand, winningTile: hand.winningTile };
  const ruleset = getRuleset(game.rulesetId);
  const problems = ruleset.validate(full, ctx, game.settings);
  if (problems.length > 0) return { kind: 'problem', problems };

  const result = ruleset.score(full, ctx, game.settings);
  if (!result.meetsMinimum) {
    if (result.rejection?.kind === 'incomplete') return { kind: 'incomplete', message: describeRejection(result) };
    return { kind: 'below_minimum', message: describeRejection(result), fan: result.effectiveFan };
  }

  let payments: Payment[];
  try {
    payments = ruleset.settle(result, ctx, game.players.map((p) => p.id), game.settings);
  } catch (e) {
    // e.g. a discard win with no discarder chosen yet: not a scoring bug, just a form that is not finished.
    return { kind: 'problem', problems: [e instanceof Error ? e.message : 'This hand cannot be settled yet.'] };
  }

  // The same shape `recordWin` would store, so the existing "how the points work out" text can narrate it unchanged.
  const round: RoundRecord = {
    index: game.rounds.length + 1,
    outcome: 'win',
    dealer: ctx.dealer,
    prevailingWind: ctx.prevailingWind,
    winner: ctx.winner,
    winningTile: full.winningTile,
    selfDraw: ctx.selfDraw,
    ...(ctx.discarder ? { discarder: ctx.discarder } : {}),
    fan: result.fan,
    effectiveFan: result.effectiveFan,
    basePoints: result.basepoints,
    patterns: result.patterns,
    isLimit: result.isLimit,
    concealed: full.concealed,
    melds: full.melds,
    flowers: full.flowers ?? [],
    ...(full.claimedSet ? { claimedSet: true } : {}),
    payments,
    recordedAt: 0,
  };
  return { kind: 'ok', result, payments, round };
}
